import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import { useEffect, useRef } from "react";

/**
 * ---- Internal caches ----
 */
const consumerCache = new Map<string, Consumer>();
const subscriptionCache = new Map<string, Subscription>();
const refCount = new Map<string, number>();

type CableUrl = string | (() => string);

/**
 * Create or reuse a Consumer, keyed by a caller-stable string.
 * `url` may be a function so each (re)connect re-evaluates the latest ticket.
 */
function getConsumer(key: string, url: CableUrl, protocols?: string[]): Consumer {
  if (!consumerCache.has(key)) {
    consumerCache.set(key, createConsumer(url as unknown as string, protocols));
  }
  return consumerCache.get(key)!;
}

function subscriptionKey(channel: string, params: Record<string, unknown>) {
  return `${channel}:${JSON.stringify(params)}`;
}

/**
 * Public hook
 */
export function useActionCable<TPayload>(options: {
  url: CableUrl;
  channel: string;
  params?: Record<string, unknown>;
  protocols?: string[];
  enabled?: boolean;
  onReceived: (data: TPayload) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}) {
  const {
    url,
    channel,
    params = {},
    protocols,
    enabled = true,
    onReceived,
    onConnected,
    onDisconnected,
  } = options;

  const receivedRef = useRef(onReceived);
  receivedRef.current = onReceived;
  const connectedRef = useRef(onConnected);
  connectedRef.current = onConnected;
  const disconnectedRef = useRef(onDisconnected);
  disconnectedRef.current = onDisconnected;

  const paramsKey = JSON.stringify(params);
  const protocolsKey = protocols?.join(",") ?? "";

  useEffect(() => {
    if (!enabled) return;

    const consumerKey = `${channel}:${paramsKey}|${protocolsKey}`;
    const consumer = getConsumer(consumerKey, url, protocols);
    const key = subscriptionKey(channel, params);

    if (!subscriptionCache.has(key)) {
      const subscription = consumer.subscriptions.create(
        { channel, ...params },
        {
          connected: () => connectedRef.current?.(),
          disconnected: () => disconnectedRef.current?.(),
          received: (data: TPayload) => receivedRef.current(data),
        }
      );

      subscriptionCache.set(key, subscription);
      refCount.set(key, 1);
    } else {
      refCount.set(key, (refCount.get(key) || 0) + 1);
    }

    return () => {
      const count = (refCount.get(key) || 1) - 1;
      refCount.set(key, count);

      if (count <= 0) {
        subscriptionCache.get(key)?.unsubscribe();
        subscriptionCache.delete(key);
        refCount.delete(key);
      }
    };
    // url is intentionally excluded: it is a stable function read on each (re)connect.
  }, [enabled, channel, paramsKey, protocolsKey]);
}
